import { skipToken, useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import type { InferTransformedProcedureOutput } from "@goose/api";

import { AIDisclosure } from "~/components/AIDisclosure";
import { CollapsibleText } from "~/components/CollapsibleText";

import {
  type EvaluationFilter,
  EvaluationsList,
} from "~/components/EvaluationsList";

import { OpportunityDetails } from "~/components/OpportunityDetails";
import { OpportunityRequirementChat } from "~/components/OpportunityRequirementChat";
import { PendingContent } from "~/components/PendingContent";
import { RequirementCards } from "~/components/RequirementCards";
import { SignOut } from "~/components/SignOut";
import { TRPCClientErrorDisplay } from "~/components/TRPCClientErrorDisplay";
import { Spinner } from "~/components/ui/Spinner";
import { OpportunityIndex } from "~/lib/search/opportunity";
import { SearchIndexContext } from "~/lib/search/search-index";
import { renderTitle } from "~/lib/title";
import { useTRPC } from "~/lib/trpc";

export const Route = createFileRoute(
  "/_authenticated/opportunity/$opportunityId/",
)({
  component: OpportunityPage,
});

function OpportunityPage() {
  const trpc = useTRPC();
  const { opportunityId } = Route.useParams();

  const [evaluationFilter, setEvaluationFilter] =
    useState<EvaluationFilter | null>(null);

  const [modifiedRequirementSet, setModifiedRequirementSet] =
    useState<InferTransformedProcedureOutput<"opportunities.requirements.modify"> | null>(
      null,
    );

  const query = useQuery(
    trpc.opportunities.requirements.extract.queryOptions({ id: opportunityId }),
  );

  const modifyRequirementsMutation = useMutation(
    trpc.opportunities.requirements.modify.mutationOptions(),
  );

  const currentRequirementSet =
    modifiedRequirementSet ?? query.data?.requirementSet;
  const currentRequirements = currentRequirementSet?.requirements;

  const isRequirementsLoading =
    query.isFetching || modifyRequirementsMutation.isPending;

  const modifyRequirements = async (modification: string) => {
    if (!currentRequirementSet?.id) {
      throw new Error("No current requirement set");
    }

    const requirementSet = await modifyRequirementsMutation.mutateAsync({
      modification,
      requirementSetId: currentRequirementSet.id,
    });

    setModifiedRequirementSet(requirementSet);
    return requirementSet.requirements;
  };

  const clarificationRequirements =
    currentRequirements?.filter((req) => req.needsClarification) ?? [];

  const searchIndexes = useMemo(() => {
    if (!query.data) return {};
    return { opportunity: new OpportunityIndex(query.data.opportunity) };
  }, [query.data]);

  const evaluationsQuery = useQuery(
    trpc.contacts.evaluations.listConnected.queryOptions(
      currentRequirementSet?.id == null
        ? skipToken
        : { requirementSetId: currentRequirementSet.id },
    ),
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Condensed Header */}
        <div className="flex items-center gap-3 mb-4">
          <img
            src="/web-app-manifest-512x512.png"
            alt="Goose"
            width={28}
            height={28}
            className="inline-block"
          />
          <div>
            <div className="text-xl font-semibold text-gray-900">Goose</div>
            <div className="text-xs text-gray-600">
              The Account Manager&apos;s AI Wingman
            </div>
          </div>
        </div>

        <AIDisclosure />

        {query.isLoading && (
          <div className="mt-6 flex items-center justify-center gap-3 text-gray-600">
            <Spinner size="md" />
            <span>Extracting requirements…</span>
          </div>
        )}

        {query.error && (
          <div className="mt-6">
            <TRPCClientErrorDisplay error={query.error} />
          </div>
        )}

        {query.data && currentRequirements && (
          <SearchIndexContext.Provider value={searchIndexes}>
            <title>
              {renderTitle(
                query.data.opportunity.title
                  ? `${query.data.opportunity.title} (${query.data.opportunity.shortId})`
                  : query.data.opportunity.shortId,
              )}
            </title>

            <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left column: Requirements */}
              <div className="space-y-4 lg:col-span-2">
                <div className="bg-white shadow-sm ring-1 ring-gray-200 rounded-lg p-5">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Job requirements
                  </h2>
                  <p className="mt-1 text-sm text-gray-600">
                    We&rsquo;ve summarized the job&rsquo;s key requirements.
                    Let&rsquo;s work together to get this req automation-ready.
                  </p>
                </div>

                {clarificationRequirements.length > 0 && (
                  <PendingContent
                    className="bg-white shadow-sm ring-1 ring-amber-200 rounded-lg p-5"
                    isPending={isRequirementsLoading}
                  >
                    <h3 className="text-base font-semibold text-amber-800">
                      Needs clarification ({clarificationRequirements.length})
                    </h3>
                    <p className="mt-1 text-sm text-amber-700">
                      These items may require follow-up with the customer.
                    </p>
                    <div className="mt-3 space-y-3">
                      {clarificationRequirements.map((req) => (
                        <div
                          key={req.id}
                          className="rounded-md border border-amber-200 bg-amber-50 p-3"
                        >
                          <div className="font-medium text-amber-900">
                            {req.name}
                          </div>
                          {req.needsClarification && (
                            <CollapsibleText
                              className="mt-1 text-sm"
                              text={req.needsClarification}
                              preserveWhitespace
                              previewChars={180}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </PendingContent>
                )}

                <PendingContent
                  className="bg-white shadow-sm ring-1 ring-gray-200 rounded-lg p-5"
                  isPending={isRequirementsLoading}
                >
                  <RequirementCards
                    requirements={currentRequirements}
                    evaluations={evaluationsQuery.data}
                    evaluationFilter={evaluationFilter}
                    onFilterChange={setEvaluationFilter}
                  />
                </PendingContent>
              </div>

              {/* Right column: Details */}
              <div className="space-y-4">
                <EvaluationsList
                  opportunityId={opportunityId}
                  requirementSet={currentRequirementSet}
                  filter={evaluationFilter}
                  onFilterChange={setEvaluationFilter}
                />

                <OpportunityDetails opportunity={query.data.opportunity} />
              </div>
            </div>

            <OpportunityRequirementChat
              requirementSetId={currentRequirementSet.id}
              onModify={modifyRequirements}
              unread={clarificationRequirements.length}
            />
          </SearchIndexContext.Provider>
        )}
      </div>

      <div className="mt-2 text-center">
        <SignOut />
      </div>
    </div>
  );
}
